/**
 * ExamWindowSanityCheck — small admin widget that flags the date-only
 * `end_date` trap fixed in iter-351 + a couple of other obvious window
 * misconfigurations. Offers one-click fixes that PATCH the exam.
 *
 * Triggers (any one of these surfaces the banner):
 *   - `end_date` is missing (admin shipped exam without a close date).
 *   - `end_date` is date-only AND parses to today/past in the user's
 *     timezone — the iter-351 trap.
 *   - `end_date` is in the past (window already closed).
 *   - `start_date` > `end_date`.
 *
 * One-click fixes:
 *   - "Set end-of-day": rewrites end_date to today as a full ISO
 *     datetime at 23:59:59 UTC (matches the backend parser).
 *   - "Extend by 24h": rewrites end_date to (now + 24h) as ISO.
 *
 * Both fixes hit the existing PUT /api/school/tenants/{tid}/exams/{eid}
 * endpoint — no new backend route needed.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Clock, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';

function parseTs(v, endOfDayForDateOnly = false) {
  if (!v) return null;
  const s = String(v);
  if (s.length === 10 && s[4] === '-' && s[7] === '-') {
    const d = new Date(s + (endOfDayForDateOnly ? 'T23:59:59Z' : 'T00:00:00Z'));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export default function ExamWindowSanityCheck({ tenantId, exam, onFixed }) {
  const qc = useQueryClient();
  const [hideUntilSave, setHideUntilSave] = useState(false);

  const patchMut = useMutation({
    mutationFn: (patch) => api.put(`/school/tenants/${tenantId}/exams/${exam?.id || exam?._id}`, patch).then(r => r.data),
    onSuccess: () => {
      toast.success('Exam window updated');
      setHideUntilSave(true);
      qc.invalidateQueries({ queryKey: ['school-exam-detail'] });
      qc.invalidateQueries({ queryKey: ['school-exams'] });
      if (onFixed) onFixed();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!exam) return null;

  const issues = [];
  const endRaw = exam.end_date || exam.available_until || '';
  const startRaw = exam.start_date || exam.available_from || '';
  const isDateOnly = endRaw && String(endRaw).length === 10;
  const closeDt = parseTs(endRaw, true);
  const openDt = parseTs(startRaw, false);
  const now = new Date();

  if (!endRaw) {
    issues.push({
      level: 'error', key: 'no-close',
      title: 'No close date set',
      detail: 'Students will see this exam forever. Set an end_date so the window actually closes.',
    });
  } else if (closeDt && closeDt < now) {
    issues.push({
      level: 'error', key: 'past-close',
      title: 'Exam window has already closed',
      detail: `Closed ${closeDt.toLocaleString()}. Students will be blocked at the "Take Exam" step.`,
    });
  } else if (isDateOnly) {
    issues.push({
      level: 'warn', key: 'date-only',
      title: 'Close date is date-only',
      detail: `Saved as "${endRaw}". Backend treats this as end-of-day UTC (${closeDt?.toLocaleString()}). If your students are in a timezone east of UTC this can close the exam earlier than expected. Consider an explicit ISO datetime.`,
    });
  }
  if (openDt && closeDt && openDt > closeDt) {
    issues.push({
      level: 'error', key: 'inverted',
      title: 'Start date is after end date',
      detail: `Opens ${openDt.toLocaleString()} but closes ${closeDt.toLocaleString()}.`,
    });
  }

  if (issues.length === 0 || hideUntilSave) return null;

  const setEndOfDay = () => {
    // Today 23:59:59 UTC as full ISO so the parser stops guessing.
    const today = new Date();
    today.setUTCHours(23, 59, 59, 0);
    patchMut.mutate({ end_date: today.toISOString(), available_until: today.toISOString() });
  };
  const extend24h = () => {
    const ts = new Date(Date.now() + 24 * 60 * 60 * 1000);
    patchMut.mutate({ end_date: ts.toISOString(), available_until: ts.toISOString() });
  };

  const isError = issues.some(i => i.level === 'error');

  return (
    <div className={`border-l-4 rounded-r p-3 ${isError ? 'border-rose-500 bg-rose-50' : 'border-amber-500 bg-amber-50'}`}
         data-testid="exam-window-sanity">
      <div className="flex items-start gap-2">
        {isError ? <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                  : <Clock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            {isError ? 'Exam window needs attention' : 'Heads-up about this exam window'}
          </p>
          <ul className="text-xs text-slate-700 mt-1 space-y-1">
            {issues.map(i => (
              <li key={i.key} data-testid={`exam-window-issue-${i.key}`}>
                <span className="font-medium">{i.title}.</span> {i.detail}
              </li>
            ))}
          </ul>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button size="sm" variant="outline" disabled={patchMut.isPending}
              onClick={setEndOfDay} data-testid="exam-window-fix-eod">
              <Wand2 className="h-3.5 w-3.5 mr-1" /> Set end-of-day (today)
            </Button>
            <Button size="sm" variant="outline" disabled={patchMut.isPending}
              onClick={extend24h} data-testid="exam-window-fix-24h">
              <Wand2 className="h-3.5 w-3.5 mr-1" /> Extend by 24h
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setHideUntilSave(true)}>Dismiss</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
